import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface CentroCusto {
  id: number;
  descricao?: string;
  referencia?: string;
  valorMaxUtilizacao: number;
  percentualMaxUtilizacao: number;
  percentualMaxAviso: number;
  dtMaxUtilizacao?: string | null;
  contaContabilId?: number | null;
  isAtivo: boolean;
  compoemCmv: boolean;
  compoemCustoGerencial: boolean;
  compoemCustoArmazena: boolean;
}

export interface CentroCustoFormData {
  descricao?: string;
  referencia?: string;
  valorMaxUtilizacao?: number;
  percentualMaxUtilizacao?: number;
  percentualMaxAviso?: number;
  dtMaxUtilizacao?: string | null;
  contaContabilId?: number | null;
  isAtivo?: boolean;
  compoemCmv?: boolean;
  compoemCustoGerencial?: boolean;
  compoemCustoArmazena?: boolean;
}

export function useCentrosCusto() {
  const queryClient = useQueryClient();

  const { data: centrosCusto = [], isLoading } = useQuery({
    queryKey: ["centros-custo"],
    queryFn: async () => {
      const { data } = await apiClient.get<CentroCusto[]>("/api/centros-custo");
      return data;
    },
  });

  const createCentroCusto = useMutation({
    mutationFn: async (formData: CentroCustoFormData) => {
      const { data } = await apiClient.post<CentroCusto>("/api/centros-custo", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centros-custo"] });
      toast.success("Centro de custo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar centro de custo: " + error.message);
    },
  });

  const updateCentroCusto = useMutation({
    mutationFn: async ({ id, ...formData }: CentroCustoFormData & { id: number }) => {
      const { data } = await apiClient.put<CentroCusto>(`/api/centros-custo/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centros-custo"] });
      toast.success("Centro de custo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar centro de custo: " + error.message);
    },
  });

  const deleteCentroCusto = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/centros-custo/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["centros-custo"] });
      toast.success("Centro de custo inativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar centro de custo: " + error.message);
    },
  });

  return { centrosCusto, isLoading, createCentroCusto, updateCentroCusto, deleteCentroCusto };
}
